import React, { ReactNode } from 'react';

import { cn } from '@/lib/utils';

type AnchorOrigin = { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };

type StyledBadgeProps = Omit<React.ComponentProps<'span'>, 'children'> & {
  children?: ReactNode;
  /** Position of the badge dot. Defaults to top-right for notification, bottom-right for status. */
  anchorOrigin?: AnchorOrigin;
  /** Status determines the dot color. Use 'notification' for red notification dot. */
  status?: 'notification' | 'online' | 'offline' | 'away';
  /** Hide the badge dot */
  invisible?: boolean;
  className?: string;
};

const statusColors = {
  notification: 'bg-destructive',
  online: 'bg-successful',
  offline: 'bg-foreground-muted',
  away: 'bg-warning',
} as const;

const getPositionClasses = (anchorOrigin: AnchorOrigin) => {
  if (anchorOrigin.vertical === 'bottom') {
    return anchorOrigin.horizontal === 'right' ? 'right-0 bottom-0' : 'bottom-0 left-0';
  }
  return anchorOrigin.horizontal === 'right' ? 'top-0 right-0' : 'top-0 left-0';
};

export const StyledBadge = ({
  children,
  status = 'notification',

  anchorOrigin = status === 'notification'
    ? { vertical: 'top', horizontal: 'right' }
    : { vertical: 'bottom', horizontal: 'right' },

  invisible = false,
  className,
  ref,
  ...props
}: StyledBadgeProps) => {
  const isNotification = status === 'notification';
  const positionClasses = getPositionClasses(anchorOrigin);

  return (
    <span ref={ref} className={cn('relative inline-flex', className)} {...props}>
      {children}
      {!invisible && (
        <span
          className={cn(
            'absolute rounded-full',
            statusColors[status],
            positionClasses,
            isNotification
              ? 'h-1 min-w-1'
              : 'box-content h-1.5 w-1.5 border-current ring-2 ring-background'
          )}
        />
      )}
    </span>
  );
};
